import { SocketMessageEvents } from '@shared/constants/campaign.constants';
import { CallDto } from '@shared/services/apiService/apiService';
import { SortConfig } from '@pages/Admin/Campaign/ui/types';
import { useEffect, useState } from 'react';
import { realtimeListsApi } from '@shared/services/apiService/realtimeListsApi';
import {
  acquireRealtimeSocket,
  type RealtimeSocketHandle,
} from '@shared/services/realtimeSocketManager';
import { useAppDispatch } from '@shared/store';
import { useGetCalls } from './useGetCalls';
import { CallsFilterFormValue } from '../model/type';

interface WsException {
  status: string;
  message: string;
}

export const useWebSocketGetCalls = (
  campaignId: string,
  params: CallsFilterFormValue,
  sortConfig: SortConfig,
  onError?: <T>(error: T) => void,
) => {
  const [realtime, setRealtime] = useState<RealtimeSocketHandle | null>(null);
  const dispatch = useAppDispatch();

  const {
    recipientRefetch,
    callsData,
    upsertCall,
    recipientIsLoading: isLoading,
    hasMore,
    loadMore,
    totalItems,
    isFetching,
  } = useGetCalls(campaignId, params, sortConfig);

  useEffect(() => {
    const handle = acquireRealtimeSocket(import.meta.env.VITE_API_URL, '/conversations');
    setRealtime(handle);

    return () => {
      handle.release();
      setRealtime(null);
    };
  }, []);

  useEffect(() => {
    if (!realtime || !campaignId) return;

    return realtime.joinRoom(SocketMessageEvents.SUB_TO_GET_CALLS, { campaignId });
  }, [realtime, campaignId]);

  useEffect(() => {
    if (!realtime) return;

    const onGetCalls = (value: { result: CallDto }) => {
      // The shared socket stays in the rooms of campaigns opened before, until it closes
      if (value.result?.customer?.campaignId !== campaignId) return;

      upsertCall(value.result);
    };

    const onException = (error: WsException) => {
      console.error(error);
      onError?.(error);
    };

    const unsubscribers = [
      realtime.on(SocketMessageEvents.GET_CALLS, onGetCalls),
      realtime.on('exception', onException),
      realtime.onMissedPushes(() =>
        dispatch(realtimeListsApi.util.invalidateTags([{ type: 'CampaignCalls', id: campaignId }])),
      ),
    ];

    return () => unsubscribers.forEach((off) => off());
  }, [realtime, campaignId, dispatch, onError, upsertCall]);

  return {
    isLoading,
    isFetching,
    hasMore,
    loadMore,
    totalItems,
    recipients: callsData,
    recipientRefetch,
  };
};
